import * as React from 'react';

// CSS & SCSS
import './assets/fonts/inter.css';
import './assets/fonts/material.css';
import './assets/fonts/icofont/icofont.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/sass/styles.scss';

// JS & COMPONENTS
import './i18n';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from './context/Themes';
import { SidebarProvider } from './context/Sidebar';
import { LoaderProvider } from './context/Preloader';
import { TranslatorProvider } from './context/Translator';
import { AuthProvider, RequireAuth, RequireAdmin } from './context/AuthProvider';
import { PendingApprovalsProvider } from './context/PendingApprovals';
import { BranchProvider } from './context/BranchContext';
import { registerRouter } from './utils/routerBridge';
import NativeNotifications from './components/NativeNotifications';
import { createBrowserRouter, RouterProvider, Navigate, useLocation } from 'react-router-dom';
import { LoginPage, RegisterPage, ForgotPasswordPage } from './pages/auth';
import { DocumentationPage, ChangeLogPage, ErrorPage } from './pages/others';
import { AvatarPage, AlertPage, ButtonPage, ChartPage, TablePage, FieldPage, HeadingPage, ColorPage } from './pages/ui';
import {
  DashboardPage,
  AnalyticsPage,
  CRMPage,
  UserListPage,
  MessageLogPage,
  UserProfilePage,
  MyAccountPage,
  ProductListPage,
  ProductViewPage,
  ProductUploadPage,
  InvoiceListPage,
  InvoiceDetailsPage,
  OrderListPage,
  ReservationManagementPage,
  ArrivalCheckRequestPage,
  MessagePage,
  NotificationPage,
  BlankPage,
  ShiftTemplatePage,
  AttendancePage,
  AdminShiftManagement,
  BranchSettingsPanel,
  TaskConfigPage,
  AdminMenuManagementPage,
  BankAccountManagementPage,
  CashierManagementPage,
  CashierTracingPage,
  ConfigurationPage,
  AiAccountsPage,
  FeatureFlagPage,
  TaskAdminReviewPage,
  TaskHistoryStatsPage,
  TaskDurationAnalysisPage,
  ShiftPreferenceReviewPage,
  EmployeeWeekendTrackingPage,
  TltPenaltyReviewPage,
  NewHireTrainingPage,
  AdminShiftReviewPage,
  ShiftReviewListPage,
  QcQuotaSetupPage,
  QcSessionManagementPage,
  QcSessionDetailPage,
  QcResultsPage,
  IssueReportManagementPage,
  CustomerFeedbackPage,
  EmployeeTltHistoryPage,
} from './pages/main';
import PayrollListPage from './pages/main/PayrollListPage';
import SalaryAdvanceRequestsPage from './pages/main/SalaryAdvanceRequestsPage';
import OrderDeletionAuditPage from './pages/main/OrderDeletionAuditPage';
import CustomerPhoneViewLogPage from './pages/main/CustomerPhoneViewLogPage';
import HolidayManagementPage from './pages/main/HolidayManagementPage';
import MenuItemRevenueManagementPage from './pages/main/MenuItemRevenueManagementPage';
import PagesManagementPage from './pages/main/PagesManagementPage';
import LoyaltyManagementPage from './pages/main/LoyaltyManagementPage';
import RandomRewardPage from './pages/main/RandomRewardPage';
import RevenueThresholdPage from './pages/main/RevenueThresholdPage';
import PointManagementPage from './pages/main/PointManagementPage';
import CustomerAnalyticsPage from './pages/main/CustomerAnalyticsPage';
import InventoryManagementPage, { InventoryLegacyRedirect } from './pages/main/InventoryManagementPage';
import CreatePurchaseOrderPage from './pages/main/CreatePurchaseOrderPage';
import PurchaseOrderDetailPage from './pages/main/PurchaseOrderDetailPage';
import SendSupplierOrderZaloPage from './pages/main/SendSupplierOrderZaloPage';
import ShopeeShoppingListPage from './pages/main/ShopeeShoppingListPage';
import SupplierOrderNeedsPage from './pages/main/SupplierOrderNeedsPage';
import BroadcastNotificationPanel from './pages/main/BroadcastNotificationPanel';
import AutoApproveSettingsPage from './pages/main/AutoApproveSettingsPage';
import ShiftRevenueRankingPage from './pages/main/ShiftRevenueRankingPage';
import SupplierOrderChangeReviewPage from './pages/main/SupplierOrderChangeReviewPage';
import ShiftHandoverRequestsPage from './pages/main/ShiftHandoverRequestsPage';
import AiMonitoringAlertsPage from './pages/main/AiMonitoringAlertsPage';
import AiRegionSplitPage from './pages/main/AiRegionSplitPage';
import { ToastContainer } from 'react-toastify';
import { WebSocketProvider } from './context/WebSocketProvider';

/** Chuyển hướng giữ nguyên chuỗi truy vấn (link sâu cũ vẫn mở đúng chi nhánh). */
const KeepQueryRedirect = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

const router = createBrowserRouter([
  // PUBLIC ROUTES
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/error', element: <ErrorPage /> },
  // #182152 — Trang NCC tự sửa/xoá đơn NVL ĐÃ CHUYỂN sang kho MENU (`truc1230/on-coffee-menu`,
  // route `/supplier-order/e/[token]`) theo lệnh sếp: trang này của NGƯỜI NGOÀI công ty, phải nằm
  // ở on-coffee.com chứ không phải app quản trị. Màn DUYỆT `/inventory/supplier-order-changes`
  // vẫn ở đây. Biến `SUPPLIER_PORTAL_URL` phía server phải trỏ về app menu.

  // PROTECTED ROUTES
  {
    path: '/bank-accounts',
    element: (
      <RequireAuth>
        <BankAccountManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/task-admin-review',
    element: (
      <RequireAuth>
        <TaskAdminReviewPage />
      </RequireAuth>
    ),
  },
  {
    path: '/task-history-stats',
    element: (
      <RequireAuth>
        <TaskHistoryStatsPage />
      </RequireAuth>
    ),
  },
  // Bàn giao công việc cho ca sau (có duyệt): ADMIN/MANAGER duyệt yêu cầu của nhân viên.
  {
    path: '/shift-handover-requests',
    element: (
      <RequireAuth>
        <ShiftHandoverRequestsPage />
      </RequireAuth>
    ),
  },
  // #123903 — xem kết quả khảo sát thời gian task (#142431) + máy tự chỉ lượt khai đáng ngờ.
  {
    path: '/task-duration-analysis',
    element: (
      <RequireAuth>
        <TaskDurationAnalysisPage />
      </RequireAuth>
    ),
  },
  {
    path: '/cashier-management',
    element: (
      <RequireAuth>
        <CashierManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/cashier-tracing',
    element: (
      <RequireAuth>
        <CashierTracingPage />
      </RequireAuth>
    ),
  },
  {
    path: '/configurations',
    element: (
      <RequireAuth>
        <ConfigurationPage />
      </RequireAuth>
    ),
  },
  {
    // #133517 — màn quản trị tài khoản nhà cung cấp AI, chỉ ADMIN.
    path: '/ai-accounts',
    element: (
      <RequireAdmin>
        <AiAccountsPage />
      </RequireAdmin>
    ),
  },
  {
    // #120646 — menu riêng cho công tắc bật/tắt tính năng mới (tách khỏi màn Cấu hình hệ thống).
    path: '/feature-flags',
    element: (
      <RequireAuth>
        <FeatureFlagPage />
      </RequireAuth>
    ),
  },
  {
    path: '/tasks',
    element: (
      <RequireAuth>
        <TaskConfigPage />
      </RequireAuth>
    ),
  },
  {
    path: '/qc-quota-setup',
    element: (
      <RequireAuth>
        <QcQuotaSetupPage />
      </RequireAuth>
    ),
  },
  {
    path: '/qc-sessions',
    element: (
      <RequireAuth>
        <QcSessionManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/qc-sessions/:sessionId',
    element: (
      <RequireAuth>
        <QcSessionDetailPage />
      </RequireAuth>
    ),
  },
  {
    path: '/qc-results',
    element: (
      <RequireAuth>
        <QcResultsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/issue-reports',
    element: (
      <RequireAuth>
        <IssueReportManagementPage />
      </RequireAuth>
    ),
  },
  // #162225 — màn xem/xử lý cảnh báo đối soát camera ↔ bill (module ai-monitoring phía server).
  {
    path: '/ai-monitoring/alerts',
    element: (
      <RequireAuth>
        <AiMonitoringAlertsPage />
      </RequireAuth>
    ),
  },
  // #223029 — KHOANH VÙNG trên ảnh camera là tính năng ADMIN (không làm ở app nhân viên).
  {
    path: '/ai-monitoring/region-split',
    element: (
      <RequireAuth>
        <AiRegionSplitPage />
      </RequireAuth>
    ),
  },
  // #181800 (lệnh sếp 18/10/2026) — bỏ màn "Gắn nhãn khu vực camera" (trùng với khoanh vùng: khung vẽ đã mang
  // tên, loại và số bàn). Link cũ trong tin Telegram chuyển sang màn khoanh vùng, giữ nguyên `?branch_id=`.
  {
    path: '/ai-monitoring/region-labels',
    element: <KeepQueryRedirect to="/ai-monitoring/region-split" />,
  },
  {
    path: '/customer-feedback',
    element: (
      <RequireAuth>
        <CustomerFeedbackPage />
      </RequireAuth>
    ),
  },
  {
    path: '/menu-management',
    element: (
      <RequireAuth>
        <AdminMenuManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/home',
    element: (
      <RequireAuth>
        <DashboardPage />
      </RequireAuth>
    ),
  },
  {
    path: '/branch-settings',
    element: (
      <RequireAuth>
        <BranchSettingsPanel />
      </RequireAuth>
    ),
  },
  {
    path: '/broadcast-notification',
    element: (
      <RequireAuth>
        <BroadcastNotificationPanel />
      </RequireAuth>
    ),
  },
  {
    path: '/auto-approve-settings',
    element: (
      <RequireAuth>
        <AutoApproveSettingsPage />
      </RequireAuth>
    ),
  },
  {
    // #130536 — bảng ca xếp doanh thu để quản lý tự gắn cờ "ca đông"
    path: '/shift-revenue-ranking',
    element: (
      <RequireAuth>
        <ShiftRevenueRankingPage />
      </RequireAuth>
    ),
  },
  {
    path: '/analytics',
    element: (
      <RequireAuth>
        <AnalyticsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/crm',
    element: (
      <RequireAuth>
        <CRMPage />
      </RequireAuth>
    ),
  },
  {
    path: '/employees',
    element: (
      <RequireAuth>
        <UserListPage />
      </RequireAuth>
    ),
  },
  {
    // #102134 — màn trả lời câu hỏi "xem lịch sử gửi tin nhắn đó ở đâu?" của sếp.
    path: '/message-logs',
    element: (
      <RequireAuth>
        <MessageLogPage />
      </RequireAuth>
    ),
  },
  {
    path: '/employees/:employeeId/tlt-history',
    element: (
      <RequireAuth>
        <EmployeeTltHistoryPage />
      </RequireAuth>
    ),
  },
  {
    path: '/orders',
    element: (
      <RequireAuth>
        <OrderListPage />
      </RequireAuth>
    ),
  },
  {
    path: '/reservations',
    element: (
      <RequireAuth>
        <ReservationManagementPage />
      </RequireAuth>
    ),
  },
  {
    // #181944 — danh sách yêu cầu xác nhận khách đến (ai chịu trách nhiệm · đã xác nhận · bỏ qua).
    path: '/arrival-checks',
    element: (
      <RequireAuth>
        <ArrivalCheckRequestPage />
      </RequireAuth>
    ),
  },
  {
    path: '/order-deletion-audit',
    element: (
      <RequireAuth>
        <OrderDeletionAuditPage />
      </RequireAuth>
    ),
  },
  {
    // #232308 — nhật ký ai xem SĐT của khách nào.
    path: '/customer-phone-view-log',
    element: (
      <RequireAuth>
        <CustomerPhoneViewLogPage />
      </RequireAuth>
    ),
  },
  {
    path: '/user-profile',
    element: (
      <RequireAuth>
        <UserProfilePage />
      </RequireAuth>
    ),
  },
  {
    path: '/my-account',
    element: (
      <RequireAuth>
        <MyAccountPage />
      </RequireAuth>
    ),
  },
  {
    path: '/product-list',
    element: (
      <RequireAuth>
        <ProductListPage />
      </RequireAuth>
    ),
  },
  {
    path: '/product-view',
    element: (
      <RequireAuth>
        <ProductViewPage />
      </RequireAuth>
    ),
  },
  {
    path: '/product-upload',
    element: (
      <RequireAuth>
        <ProductUploadPage />
      </RequireAuth>
    ),
  },
  {
    path: '/invoice-list',
    element: (
      <RequireAuth>
        <InvoiceListPage />
      </RequireAuth>
    ),
  },
  {
    path: '/invoice-details',
    element: (
      <RequireAuth>
        <InvoiceDetailsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/order-list',
    element: (
      <RequireAuth>
        <OrderListPage />
      </RequireAuth>
    ),
  },
  {
    path: '/message',
    element: (
      <RequireAuth>
        <MessagePage />
      </RequireAuth>
    ),
  },
  {
    path: '/notification',
    element: (
      <RequireAuth>
        <NotificationPage />
      </RequireAuth>
    ),
  },
  {
    path: '/shift-management',
    element: (
      <RequireAuth>
        <AdminShiftManagement />
      </RequireAuth>
    ),
  },
  {
    path: '/shift-preferences-admin-review',
    element: (
      <RequireAuth>
        <ShiftPreferenceReviewPage />
      </RequireAuth>
    ),
  },
  {
    path: '/weekend-tracking',
    element: (
      <RequireAuth>
        <EmployeeWeekendTrackingPage />
      </RequireAuth>
    ),
  },
  {
    // Admin bấm tay xét trừ TLT đăng ký ca (không còn cron tự trừ) + hoàn TLT.
    path: '/tlt-penalty-review',
    element: (
      <RequireAdmin>
        <TltPenaltyReviewPage />
      </RequireAdmin>
    ),
  },
  {
    // #005135/#190609 — theo dõi nhân viên mới đang tập việc (chỉ nhận ca gãy).
    path: '/new-hire-training',
    element: (
      <RequireAuth>
        <NewHireTrainingPage />
      </RequireAuth>
    ),
  },
  {
    path: '/shift-review',
    element: (
      <RequireAuth>
        <ShiftReviewListPage />
      </RequireAuth>
    ),
  },
  {
    path: '/shifts/:shiftId/review',
    element: (
      <RequireAuth>
        <AdminShiftReviewPage />
      </RequireAuth>
    ),
  },
  {
    path: '/shift-templates',
    element: (
      <RequireAuth>
        <ShiftTemplatePage />
      </RequireAuth>
    ),
  },
  {
    path: '/attendance',
    element: (
      <RequireAuth>
        <AttendancePage />
      </RequireAuth>
    ),
  },
  {
    path: '/payroll',
    element: (
      <RequireAuth>
        <PayrollListPage />
      </RequireAuth>
    ),
  },
  {
    path: '/salary-advance-requests',
    element: (
      <RequireAuth>
        <SalaryAdvanceRequestsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/menu-item-revenue',
    element: (
      <RequireAuth>
        <MenuItemRevenueManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/revenue-threshold',
    element: (
      <RequireAuth>
        <RevenueThresholdPage />
      </RequireAuth>
    ),
  },
  {
    path: '/holidays',
    element: (
      <RequireAuth>
        <HolidayManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/pages-management',
    element: (
      <RequireAuth>
        <PagesManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/loyalty-management',
    element: (
      <RequireAuth>
        <LoyaltyManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/random-reward',
    element: (
      <RequireAuth>
        <RandomRewardPage />
      </RequireAuth>
    ),
  },
  {
    path: '/points',
    element: (
      <RequireAuth>
        <PointManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/customer-analytics',
    element: (
      <RequireAuth>
        <CustomerAnalyticsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/inventory/purchase-order/create',
    element: (
      <RequireAuth>
        <CreatePurchaseOrderPage />
      </RequireAuth>
    ),
  },
  {
    path: '/inventory/supplier-order-zalo',
    element: (
      <RequireAuth>
        <SendSupplierOrderZaloPage />
      </RequireAuth>
    ),
  },
  {
    // #002620 — "NCC đang cần đặt hàng": gom NVL dưới ngưỡng THEO NCC + thao tác theo kênh đặt.
    // Link trong tin Telegram cảnh báo tồn kho trỏ thẳng vào đây kèm ?branch=<id>.
    path: '/inventory/supplier-order-needs',
    element: (
      <RequireAuth>
        <SupplierOrderNeedsPage />
      </RequireAuth>
    ),
  },
  {
    // #233654 — "Cần mua qua Shopee" (danh sách đi chợ, không phải đơn đặt hàng).
    // Route riêng, KHÔNG nhét vào /inventory/:tabKey để không đụng InventoryManagementPage.
    path: '/inventory/shopee-shopping',
    element: (
      <RequireAuth>
        <ShopeeShoppingListPage />
      </RequireAuth>
    ),
  },
  {
    path: '/inventory/supplier-order-changes',
    element: (
      <RequireAuth>
        <SupplierOrderChangeReviewPage />
      </RequireAuth>
    ),
  },
  {
    path: '/inventory/purchase-order/:id',
    element: (
      <RequireAuth>
        <PurchaseOrderDetailPage />
      </RequireAuth>
    ),
  },
  {
    // Route cũ giữ lại chỉ để redirect (?tab=xxx -> /inventory/xxx) — không rơi link cũ, xem #194334.
    path: '/inventory',
    element: (
      <RequireAuth>
        <InventoryLegacyRedirect />
      </RequireAuth>
    ),
  },
  {
    path: '/inventory/:tabKey',
    element: (
      <RequireAuth>
        <InventoryManagementPage />
      </RequireAuth>
    ),
  },
  {
    path: '/blank-page',
    element: (
      <RequireAuth>
        <BlankPage />
      </RequireAuth>
    ),
  },
  // UI PAGES (protected)
  {
    path: '/avatars',
    element: (
      <RequireAuth>
        <AvatarPage />
      </RequireAuth>
    ),
  },
  {
    path: '/alerts',
    element: (
      <RequireAuth>
        <AlertPage />
      </RequireAuth>
    ),
  },
  {
    path: '/buttons',
    element: (
      <RequireAuth>
        <ButtonPage />
      </RequireAuth>
    ),
  },
  {
    path: '/charts',
    element: (
      <RequireAuth>
        <ChartPage />
      </RequireAuth>
    ),
  },
  {
    path: '/tables',
    element: (
      <RequireAuth>
        <TablePage />
      </RequireAuth>
    ),
  },
  {
    path: '/fields',
    element: (
      <RequireAuth>
        <FieldPage />
      </RequireAuth>
    ),
  },
  {
    path: '/headings',
    element: (
      <RequireAuth>
        <HeadingPage />
      </RequireAuth>
    ),
  },
  {
    path: '/colors',
    element: (
      <RequireAuth>
        <ColorPage />
      </RequireAuth>
    ),
  },

  // OTHER PAGES (protected)
  {
    path: '/',
    element: (
      <RequireAuth>
        <Navigate
          to="/home"
          replace
        />
      </RequireAuth>
    ),
  },
  {
    path: '/documentation',
    element: (
      <RequireAuth>
        <DocumentationPage />
      </RequireAuth>
    ),
  },
  {
    path: '/changelog',
    element: (
      <RequireAuth>
        <ChangeLogPage />
      </RequireAuth>
    ),
  },
]);

// #120936 — BranchProvider bọc NGOÀI RouterProvider nên không dùng được useSearchParams.
// Đăng ký router để context ghi ngược `?branch_id=` bằng replace mà router vẫn biết.
registerRouter(router);

createRoot(document.getElementById('root')).render(
  <ThemeProvider>
    <LoaderProvider>
      <AuthProvider>
        <BranchProvider>
          <WebSocketProvider>
            <TranslatorProvider>
              <SidebarProvider>
                {/* #231533 — nằm TRONG AuthProvider (cần user để biết có được gọi API đếm không),
                    BỌC NGOÀI router để sidebar và trang chủ dùng chung một con số. */}
                <PendingApprovalsProvider>
                  <NativeNotifications />
                  <RouterProvider router={router} />
                </PendingApprovalsProvider>
              </SidebarProvider>
            </TranslatorProvider>
          </WebSocketProvider>
        </BranchProvider>
      </AuthProvider>
    </LoaderProvider>
    <ToastContainer autoClose={500} />
  </ThemeProvider>,
);
